import { FC, useMemo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStripe } from '@stripe/react-stripe-js';

import { userSelector } from 'app/auth/store/auth.selectors';
import {
  walletCardsPendingSelector,
  walletCardsSelector,
  walletCheckoutSessionSelector,
  walletStripeWalletSelector,
} from 'app/wallet/store/wallet.selectors';

import { CreatorProfileDto } from 'types/creator-profile.type';
import { PaymentCardDto } from 'types/payment-card.type';
import { ZIndexes } from 'enums/z-indexes';
import { useTranslation } from 'react-i18next';
import { CreatorDto } from 'types/creator.type';
import { PaymentRequestDto } from 'types/payment-request-dto';
import { getSubscriptionCheckout } from 'app/wallet/store/wallet.actions';
import { cleanWallet } from 'app/wallet/store/wallet.slice';
import { SubscribeModalBox } from 'components/styled/subcribe-modal-box';
import { CenteredModal } from 'components/styled/centered-modal';
import { styled } from '@mui/material';

import Close from '@mui/icons-material/Close';
import Backdrop from '@mui/material/Backdrop';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import CardSelector from 'components/card-selector';
import WalletPayButton from 'components/wallet-pay-button';
import AppBackdrop from 'components/app-backdrop';
import { colors } from 'constants/colors';

const CloseButton = styled(IconButton)({
  position: 'absolute',
  right: '8px',
  top: '-16px',
});

const RollBackground = styled(Grid)({
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row',
  padding: '8px 4px',
  gap: '3px',
  height: '182px',
});

const RollPerk = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  background: '#272727',
  maxHeight: '164px',
  flex: '1 1 110px',
  padding: '18px 10px',
  border: '3px solid #F2F2F2',
  borderRadius: '9px',
  height: '100%',
});

const StyledTitle = styled(Typography)({
  fontSize: '46px !important',
  textShadow: `0px 4px 4px ${colors.default.textShadow}`,
  WebkitTextStroke: `1px ${colors.default.infoText}`,
  lineHeight: '78%',
});

const StyledSubtitle = styled('span')({
  color: colors.default.thinWhite,
  fontSize: '12px',
  fontWeight: 800,
});

const PerkTitle = styled(Typography)({
  fontSize: '16px !important',
  color: colors.default.info,
  textAlign: 'center',
});

interface ModalSubscribeProps {
  open: boolean;
  profile: CreatorProfileDto | CreatorDto;
  onClose: () => void;
  onSuccess: () => void;
  onErrorPayment: () => void;
}
const ModalSubscribe: FC<ModalSubscribeProps> = ({
  open,
  profile,
  onClose,
  onErrorPayment,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const { t } = useTranslation();

  const user = useSelector(userSelector);
  const cards = useSelector(walletCardsSelector);
  const pending_cards = useSelector(walletCardsPendingSelector);
  const stripeWallet = useSelector(walletStripeWalletSelector);
  const checkoutSession = useSelector(walletCheckoutSessionSelector);

  const clientSecret = useMemo(
    () => checkoutSession?.clientSecret,
    [checkoutSession]
  );

  const paymentRequest: PaymentRequestDto | null = useMemo(() => {
    if (checkoutSession) {
      return JSON.parse(checkoutSession?.request);
    }
    return null;
  }, [checkoutSession]);

  const defaultPaymentMethod = useMemo(
    () =>
      cards?.find((d) =>
        !user?.defaultPaymentMethodId
          ? false
          : d.id === user.defaultPaymentMethodId
      ),
    [user, cards]
  );

  const priceUsd = useMemo(
    () =>
      Number(!profile.roll ? 0 : profile.roll.price.amount / 100).toFixed(2),
    [profile.roll]
  );

  const nextPaymentDate: string = useMemo(() => {
    const currentDate = new Date();

    currentDate.setMonth(currentDate.getMonth() + 1);

    return currentDate.toDateString();
  }, []);

  const [state, setState] = useState('');

  const [walletPaymentMethod, setWalletPaymentMethod] = useState<string | null>(
    null
  );

  const [selectedPM, setSelectedPM] = useState<PaymentCardDto | null>(null);
  const selectedCard = useMemo<PaymentCardDto | undefined>(
    () => selectedPM || defaultPaymentMethod,
    [defaultPaymentMethod, selectedPM]
  );

  // TODO: move all to actions
  const handleSubmit = useCallback(async () => {
    if (!stripe || !selectedCard || !clientSecret) {
      return;
    }
    try {
      setState('pending');
      await stripe.confirmCardPayment(clientSecret, {
        payment_method: selectedCard.stripePaymentMethodId,
      });

      setState('success');
      onSuccess();
    } catch (e) {
      setState('rejected');
      onErrorPayment();
    }
  }, [stripe, selectedCard, clientSecret, onSuccess, onErrorPayment]);

  const handleSelectCard = (card: PaymentCardDto | string) => {
    if (typeof card === 'string') {
      setWalletPaymentMethod(card);
    } else {
      setSelectedPM(card);
      setWalletPaymentMethod(null);
    }
  };

  const handleSubscriptionCheckout = useCallback(async () => {
    await dispatch<any>(
      getSubscriptionCheckout({
        creator_id: profile.id,
        roll_id: profile.roll.id,
      })
    );
  }, [dispatch, profile]);

  useEffect(() => {
    if (open) {
      handleSubscriptionCheckout();
      return;
    }
    dispatch(cleanWallet());
  }, [handleSubscriptionCheckout, open, dispatch]);

  useEffect(() => {
    if (stripeWallet) {
      setWalletPaymentMethod(stripeWallet.applePay ? 'apple' : 'google');
    }
  }, [stripeWallet]);

  return (
    <>
      <CenteredModal open={open} onClose={onClose}>
        <SubscribeModalBox className="modal-confirm">
          <Grid container padding="0 16px" position="relative">
            <StyledTitle
              className="Modak"
              variant="h2"
              textTransform="uppercase"
            >
              {t('headers.unlock-access')}
            </StyledTitle>
            <Typography
              variant="body1"
              component="p"
              sx={{ fontFamily: 'Avenir-Regular !important' }}
            >
              <span
                style={{ color: colors.default.secondary, fontSize: '16px', fontWeight: 500 }}
              >
                ${priceUsd}
              </span>

              <StyledSubtitle>
                &nbsp;/&nbsp;{t('subtitles.per-month')}
              </StyledSubtitle>
            </Typography>
            <CloseButton onClick={onClose}>
              <Close />
            </CloseButton>
          </Grid>

          <br />

          <RollBackground className="bg-roll" container>
            <RollPerk item>
              <Typography sx={{ fontSize: '50px !important' }}>🤯</Typography>
              <PerkTitle variant="h6">{t('titles.view-camera-roll')}</PerkTitle>
            </RollPerk>
            <RollPerk item>
              <Typography sx={{ fontSize: '50px !important' }}>💬</Typography>
              <PerkTitle variant="h6">{t('titles.private-dms')}</PerkTitle>
            </RollPerk>
            <RollPerk item>
              <Typography sx={{ fontSize: '50px !important' }}>🎁</Typography>
              <PerkTitle variant="h6">{t('titles.fan-perks')}</PerkTitle>
            </RollPerk>
          </RollBackground>

          <br />

          <Grid
            container
            sx={{ padding: '0 16px 16px', flexDirection: 'column' }}
          >
            <Typography
              variant="body1"
              component="p"
              sx={{
                fontFamily: 'Avenir-Regular !important',
                color: `${colors.default.info} !important`,
                fontSize: '14px',
                padding: '0 0 8px',
              }}
            >
              {t('subheaders.support-creator')}
            </Typography>

            <CardSelector
              walletPaymentMethod={walletPaymentMethod}
              selectedCard={selectedCard}
              onCardSelected={handleSelectCard}
            />

            <br />

            {!pending_cards && selectedCard && !walletPaymentMethod && (
              <Button
                disabled={pending_cards || !user || !clientSecret}
                variant="contained"
                color="green"
                onClick={handleSubmit}
              >
                {t('buttons.subscribe')}
              </Button>
            )}

            {clientSecret && paymentRequest && (
              <WalletPayButton
                walletPaymentMethod={walletPaymentMethod}
                paymentRequestDto={paymentRequest}
                clientSecret={clientSecret}
                handleConfirmPayment={onSuccess}
                onError={onErrorPayment}
              />
            )}

            <br />

            <Typography
              variant="body1"
              component="p"
              sx={{
                color: colors.default.lightGrey,
                fontSize: '11px !important',
                fontFamily: 'Avenir-Regular !important',
                textAlign: 'center',
              }}
            >
              {t('texts.you-will-be-charged')}&nbsp;
              <span style={{ color: colors.default.info }}>${priceUsd}</span>{' '}
              {t('texts.subscribtion-renew')} &nbsp;
              <span style={{ color: colors.default.info }}>{nextPaymentDate}</span>
              &nbsp; {t('texts.unless-canceled')}
            </Typography>
          </Grid>
        </SubscribeModalBox>
      </CenteredModal>

      <Backdrop
        open={state === 'pending'}
        sx={{ zIndex: ZIndexes.External_Modal_Second }}
      >
        <AppBackdrop />
      </Backdrop>
    </>
  );
};

export default ModalSubscribe;
